import type IconPropsType from "@/lib/types/graphics/icons/IconProps.type";
import { memo, Suspense, useMemo } from "react";
import IconContent from "./IconContent";
import { placeholderIconData } from "./loadIconData";

function Icon({
  name,
  color = "primary",
  size = "lg",
  className,
  handleClick,
}: IconPropsType) {
  // Memoize style object to prevent recreation on every render
  const style = useMemo(() => {
    const resolvedSize =
      typeof size === "number" ? `${size}px` : `var(--svg-size-icon-${size})`;

    return {
      width: resolvedSize,
      height: resolvedSize,
      minWidth: resolvedSize,
      minHeight: resolvedSize,
      flexShrink: 0,
    };
  }, [size]);

  // Memoize className string to prevent concatenation on every render
  const combinedClassName = useMemo(
    () =>
      `color-icon-${color} size-icon-${typeof size === "number" ? "custom" : size}${
        className ? ` ${className}` : ""
      }`,
    [color, size, className],
  );

  return (
    <Suspense
      fallback={
        <svg
          style={{ ...style, visibility: "hidden" }}
          aria-hidden="true"
          className={combinedClassName}
          viewBox={placeholderIconData.viewBox}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        />
      }
    >
      <IconContent
        name={name || "placeholder"}
        style={style}
        className={combinedClassName}
        handleClick={handleClick}
      />
    </Suspense>
  );
}

// Prevent re-renders when props haven't changed (most impactful optimization)
export default memo(Icon);
